import { MutationResult, useApolloClient, useMutation } from '@apollo/client';
import { ExecutionResult } from 'graphql';

import {
  DeleteTimelineEntryMutation,
  DeleteTimelineEntryMutationVariables,
  PageInfo,
} from '../../../types/__generated__';
import { usePageMeta } from '../usePageMeta';
import mutation from './queries/deleteTimelineEntry.gql';
import query from './queries/listTimelineEntries.gql';
import { updateActivityTotals } from './updateActivityTotals';
import { useMeasurementSystem } from './useMeasurementSystem';
import { useTimelineEntryType } from './useTimelineEntryType';

export function useDeleteTimelineEntry(): [
  (
    id: string,
    pageInfo?: PageInfo,
  ) => Promise<ExecutionResult<DeleteTimelineEntryMutation>>,
  MutationResult<DeleteTimelineEntryMutation>,
] {
  const client = useApolloClient();
  const { pageType, pageShortName, isPreview } = usePageMeta();
  const measurementSystem = useMeasurementSystem();
  // B2C_048 - show hide fitness data
  // EntryType can be removed when experiment has ended
  // or added as a non experiment test if successful
  const { entryType } = useTimelineEntryType();

  const [runMutation, mutationResult] = useMutation<
    DeleteTimelineEntryMutation,
    DeleteTimelineEntryMutationVariables
  >(mutation, {
    update(cache, { data }) {
      const entryId = data && data.deleteTimelineEntry;

      if (!entryId) {
        return;
      }

      // This will remove this specific timeline entry from the cache.
      // However, a reference will still exist inside timeline nodes inside Page.
      // We are filtering by canRead in read and merge to hide the dangling reference
      cache.evict({ id: `TimelineEntry:${entryId}` });

      updateActivityTotals(client, pageType, pageShortName, isPreview);
    },
    context: {
      credentials: 'include',
    },
  });

  const deleteTimelineEntry = (id: string, pageInfo?: PageInfo) =>
    runMutation({
      variables: { input: { id } },
      refetchQueries:
        pageInfo?.endCursor && pageInfo?.hasNextPage
          ? [
              {
                query,
                variables: {
                  after: pageInfo.endCursor,
                  first: 1,
                  slug: pageShortName,
                  type: pageType.toUpperCase(),
                  measurementSystem,
                  entryType,
                },
              },
            ]
          : [],
    });

  return [deleteTimelineEntry, mutationResult];
}
